// User Tools Cache Service
// Implements Stale-While-Revalidate caching for user provisioned tools

import { getTools } from '../data/SQL';

const CACHE_KEY = 'tce_user_tools_cache';
const CACHE_VERSION = '1.0';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MIN_REFRESH_INTERVAL = 30 * 1000; // 30 seconds minimum between API calls
const DEFAULT_POLLING_INTERVAL = 30 * 1000; // 30 seconds between automatic checks

// Allow override for testing
const getPollingInterval = () => {
  return window.TCE_POLLING_INTERVAL || DEFAULT_POLLING_INTERVAL;
};

/**
 * Cache structure:
 * {
 *   version: string,
 *   timestamp: number,
 *   email: string,
 *   data: string, // comma-separated tools string
 *   lastUpdated: number
 * }
 */

/**
 * Get cache entry from localStorage
 */
const getCacheEntry = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const cacheEntry = JSON.parse(cached);
    
    // Check version compatibility
    if (cacheEntry.version !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return cacheEntry;
  } catch (error) {
    console.warn('Error reading user tools cache:', error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

/**
 * Set cache entry in localStorage
 */
const setCacheEntry = (email, toolsData) => {
  try {
    const cacheEntry = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      email: email.toLowerCase(),
      data: toolsData || '',
      lastUpdated: Date.now()
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
    return cacheEntry;
  } catch (error) {
    console.warn('Error saving user tools cache:', error);
    return null;
  }
};

/**
 * Check if cache is stale (expired)
 */
const isCacheStale = (cacheEntry) => {
  if (!cacheEntry) return true;
  return (Date.now() - cacheEntry.timestamp) > CACHE_TTL;
};

/**
 * Check if cache belongs to current user
 */
const isCacheForUser = (cacheEntry, email) => {
  if (!cacheEntry || !email) return false;
  return cacheEntry.email === email.toLowerCase();
};

/**
 * Background refresh function that updates cache and notifies listeners
 */
const refreshInBackground = async (email, onUpdate) => {
  try {
    // Get current cache before fetch to compare later
    const currentCache = getCacheEntry();
    const currentData = currentCache && isCacheForUser(currentCache, email) ? currentCache.data : null;
    
    const freshData = await getTools(email);
    
    // Update cache
    const updatedCache = setCacheEntry(email, freshData);
    
    // Only call onUpdate if data actually changed
    if (updatedCache && onUpdate && freshData !== currentData) {
      onUpdate(freshData);
    }
    
    return freshData;
  } catch (error) {
    console.error('Background refresh failed:', error);
    return null;
  }
};

/**
 * Main cache service class
 */
export class UserToolsCache {
  constructor() {
    this.listeners = new Set();
    this.refreshPromise = null;
    this.pollingInterval = null;
    this.currentUserEmail = null;
    this.lastPollingRefresh = 0; // Track last polling refresh separately
  }

  /**
   * Subscribe to cache updates
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Start continuous polling for tool updates
   */
  startPolling(email) {
    if (!email) return;
    
    const normalizedEmail = email.toLowerCase();
    this.currentUserEmail = normalizedEmail;
    
    // Clear any existing polling
    this.stopPolling();
    
    this.pollingInterval = setInterval(async () => {
      // Only poll if we still have the same user
      if (this.currentUserEmail !== normalizedEmail) {
        this.stopPolling();
        return;
      }
      
      const cacheEntry = getCacheEntry();
      const hasValidCache = cacheEntry && isCacheForUser(cacheEntry, normalizedEmail);
      
      // Check if enough time has passed since last polling refresh (independent of cache rate limiting)
      const timeSinceLastPollingRefresh = Date.now() - this.lastPollingRefresh;
      const canPoll = timeSinceLastPollingRefresh >= MIN_REFRESH_INTERVAL;
      
      if (hasValidCache && canPoll && !this.refreshPromise) {
        this.lastPollingRefresh = Date.now(); // Update polling timestamp
        
        this.refreshPromise = refreshInBackground(normalizedEmail, (freshData) => {
          // refreshInBackground already checked if data changed before calling this callback
          this.notifyListeners(freshData);
          this.refreshPromise = null;
        }).catch(() => {
          this.refreshPromise = null;
        });
      }
    }, getPollingInterval());
  }

  /**
   * Stop continuous polling
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Notify all subscribers of cache updates
   */
  notifyListeners(data) {
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in cache listener:', error);
      }
    });
  }

  /**
   * Get user tools with Stale-While-Revalidate strategy
   * 1. Return cached data immediately if available
   * 2. ALWAYS start background refresh to ensure fresh tools
   * 3. Update UI when fresh data becomes available
   */
  async getUserTools(email) {
    if (!email) {
      throw new Error('Email is required for getUserTools');
    }

    const normalizedEmail = email.toLowerCase();
    const cacheEntry = getCacheEntry();
    
    // Check if we have valid cache for this user
    const hasValidCache = cacheEntry && isCacheForUser(cacheEntry, normalizedEmail);
    
    // Strategy: Always refresh while serving cache
    if (hasValidCache) {
      // Serve cached data immediately
      
      // Start continuous polling for this user
      this.startPolling(normalizedEmail);
      
      // Always do an initial background refresh on page load/cache access
      // This ensures fresh data is fetched immediately when user loads the page
      if (!this.refreshPromise) {
        this.refreshPromise = refreshInBackground(normalizedEmail, (freshData) => {
          // refreshInBackground already checked if data changed before calling this callback
          this.notifyListeners(freshData);
          this.refreshPromise = null;
        }).catch(() => {
          this.refreshPromise = null;
        });
      }
      
      return cacheEntry.data;
    }
    
    // No valid cache - fetch fresh data
    try {
      const freshData = await getTools(normalizedEmail);
      setCacheEntry(normalizedEmail, freshData);
      
      // Start polling after successful fetch
      this.startPolling(normalizedEmail);
      
      return freshData;
    } catch (error) {
      console.error('Failed to fetch user tools:', error);
      throw error;
    }
  }

  /**
   * Force refresh user tools and update cache
   */
  async forceRefresh(email) {
    if (!email) {
      throw new Error('Email is required for forceRefresh');
    }

    const normalizedEmail = email.toLowerCase();
    
    try {
      const freshData = await getTools(normalizedEmail);
      setCacheEntry(normalizedEmail, freshData);
      this.notifyListeners(freshData);
      return freshData;
    } catch (error) {
      console.error('Force refresh failed:', error);
      throw error;
    }
  }

  /**
   * Clear cache for current user or all users
   */
  clearCache(email = null) {
    if (email) {
      // Clear only if cache belongs to specified user
      const cacheEntry = getCacheEntry();
      if (cacheEntry && isCacheForUser(cacheEntry, email)) {
        localStorage.removeItem(CACHE_KEY);
      }
    } else {
      // Clear all cache
      localStorage.removeItem(CACHE_KEY);
    }
    
    // Stop polling when cache is cleared
    this.stopPolling();
  }

  /**
   * Cleanup method for when user logs out or changes
   */
  cleanup() {
    this.stopPolling();
    this.listeners.clear();
    this.refreshPromise = null;
    this.currentUserEmail = null;
    this.lastPollingRefresh = 0;
  }

  /**
   * Get cache info for debugging
   */
  getCacheInfo() {
    const cacheEntry = getCacheEntry();
    if (!cacheEntry) return null;
    
    return {
      ...cacheEntry,
      isStale: isCacheStale(cacheEntry),
      age: Date.now() - cacheEntry.timestamp,
      ageFormatted: new Date(Date.now() - cacheEntry.timestamp).toISOString().substr(11, 8),
      isPolling: !!this.pollingInterval,
      currentUser: this.currentUserEmail,
      pollingIntervalMs: getPollingInterval(),
      lastPollingRefresh: this.lastPollingRefresh,
      timeSinceLastPollingRefresh: Date.now() - this.lastPollingRefresh,
      nextPollingRefreshIn: Math.max(0, MIN_REFRESH_INTERVAL - (Date.now() - this.lastPollingRefresh))
    };
  }

  /**
   * Update polling interval (for testing/debugging)
   */
  setPollingInterval(intervalMs) {
    const wasPolling = !!this.pollingInterval;
    const currentUser = this.currentUserEmail;
    
    if (wasPolling) {
      this.stopPolling();
    }
    
    // Update the constant (this is a bit hacky but works for testing)
    Object.defineProperty(window, 'TCE_POLLING_INTERVAL', { value: intervalMs, writable: true });
    
    if (wasPolling && currentUser) {
      // Restart polling with new interval
      setTimeout(() => this.startPolling(currentUser), 100);
    }
  }
}

// Export singleton instance
export const userToolsCache = new UserToolsCache();

// Add global helper for testing (available in browser console)
window.setToolsPollingInterval = (seconds) => {
  const intervalMs = seconds * 1000;
  window.TCE_POLLING_INTERVAL = intervalMs;
  userToolsCache.setPollingInterval(intervalMs);
};

// Add global helper to check polling status
window.checkToolsPolling = () => {
  const info = userToolsCache.getCacheInfo();
  console.log('Tools Polling Status:', {
    isPolling: info?.isPolling || false,
    pollingInterval: `${(info?.pollingIntervalMs || 0) / 1000}s`,
    lastPollingRefresh: info?.lastPollingRefresh ? new Date(info.lastPollingRefresh).toLocaleTimeString() : 'Never',
    nextRefreshIn: `${Math.round((info?.nextPollingRefreshIn || 0) / 1000)}s`,
    currentUser: info?.currentUser || 'None'
  });
  return info;
};

// Export utility functions for direct use if needed
export {
  getCacheEntry,
  setCacheEntry,
  isCacheStale,
  isCacheForUser
};